// ─────────────────────────────────────────────────────────────────────────────
// App.tsx — Root application component
//
// Sets up React Router routes inside the Layout shell.
// Lazy-loads pages to keep the initial bundle small (code splitting).
// ─────────────────────────────────────────────────────────────────────────────

import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';

// Lazy-load page components — each becomes its own JS chunk.
// Pages use named exports, so we extract the export inside .then()
// to satisfy React.lazy's requirement for a { default } shape.
const CVPage       = lazy(() => import('@/pages/CVPage').then(m => ({ default: m.CVPage })));
const PatternsPage = lazy(() => import('@/pages/PatternsPage').then(m => ({ default: m.PatternsPage })));
const DockerPage   = lazy(() => import('@/pages/DockerPage').then(m => ({ default: m.DockerPage })));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const SweepstakeLayout     = lazy(() => import('@/pages/projects/worldCupSweepstake/SweepstakeLayout').then(m => ({ default: m.SweepstakeLayout })));
const SweepstakeQualifying = lazy(() => import('@/pages/projects/worldCupSweepstake/SweepstakeQualifying').then(m => ({ default: m.SweepstakeQualifying })));
const SweepstakeFinals     = lazy(() => import('@/pages/projects/worldCupSweepstake/SweepstakeFinals').then(m => ({ default: m.SweepstakeFinals })));
const SweepstakeHistory    = lazy(() => import('@/pages/projects/worldCupSweepstake/SweepstakeHistory').then(m => ({ default: m.SweepstakeHistory })));

// Inline loading fallback — minimalist, matches the dark hero
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-hero)' }}>
    <div className="flex gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="w-2.5 h-2.5 rounded-full bg-accent animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  </div>
);

// 404 element — extracted to keep JSX readable
const NotFound: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center text-center px-6" style={{ backgroundColor: 'var(--color-hero)' }}>
    <div>
      <h1 className="font-display text-6xl font-black text-accent mb-4">404</h1>
      <p className="text-slate-400 mb-6">Page not found.</p>
      <a href="/" className="btn-gold inline-flex">Go Home</a>
    </div>
  </div>
);

// Wrap each lazy page in its own Suspense so other routes don't block
const withSuspense = (el: React.ReactElement) => (
  <Suspense fallback={<PageLoader />}>{el}</Suspense>
);

const App: React.FC = () => {
  return (
    <Routes>
      {/*
        Layout route — React Router v6 layout pattern.
        Layout renders <Outlet /> where child routes appear.
        This is different from wrapping <Layout> around children;
        here React Router controls the render lifecycle.
      */}
      <Route element={<Layout />}>
        <Route path="/"         element={withSuspense(<CVPage />)} />
        <Route path="/patterns" element={withSuspense(<PatternsPage />)} />
        <Route path="/docker"   element={withSuspense(<DockerPage />)} />
        <Route path="/projects" element={withSuspense(<ProjectsPage />)} />
        <Route path="/projects/world-cup-sweepstake-2026" element={withSuspense(<SweepstakeLayout />)}>
          <Route index             element={<SweepstakeQualifying />} />
          <Route path="finals"     element={<SweepstakeFinals />} />
          <Route path="history"    element={<SweepstakeHistory />} />
        </Route>
        <Route path="*"         element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default App;
